<template>
  <div :class="['dashboard-vendedor', { 'dashboard-dark': isDark }]">
    <!-- Header Vendedor Profile -->
    <div class="profile-section mb-4">
      <div class="profile-card">
        <div class="d-flex align-items-center mb-2">
          <b-avatar
            :text="getIniciais(vendedor.NOME)"
            size="3.8rem"
            class="profile-avatar shadow-sm mr-3"
            :variant="getCor(vendedor.CODIGO)"
          />
          <div class="profile-info">
            <h4 class="name mb-0">{{vendedor.NOME}}</h4>
            <div class="meta-label">META: <span>{{vendedor.TX_META}}</span></div>
          </div>
        </div>
        <div class="progress-container">
          <div class="d-flex justify-content-between mb-1 small">
            <span>Mês: {{vendedor.TX_TOTAL_VENDA}}</span>
            <span class="font-weight-bold">{{vendedor.TX_PERCENTUAL_META}}%</span>
          </div>
          <b-progress 
            class="progress-modern" 
            height="10px" 
            :variant="colorirPercentualMeta()" 
            :value="vendedor.PERCENTUAL_META"
            animated
          />
        </div>
      </div>
    </div>

    <!-- Ações Rápidas -->
    <div class="actions-grid mb-4">
      <div class="action-item bg-warning-soft" @click="consultarCliente()">
        <div class="action-icon">
          <i class="fa fa-search text-warning"></i>
        </div>
        <span class="action-label">Consultar Cliente</span>
      </div>
      <div class="action-item bg-success-soft" @click="abrirNovoPedido()">
        <div class="action-icon">
          <i class="fa fa-plus text-success"></i>
        </div>
        <span class="action-label">Novo Pedido</span>
      </div>
      <div class="action-item bg-primary-soft" @click.stop.prevent="imprimirVendas()">
        <div class="action-icon">
          <i class="fa fa-money text-primary"></i>
        </div>
        <div class="action-data">
          <span class="value">{{vendedor.TX_VL_CLIENTE_MAIS_COMPROU}}</span>
          <span class="label">Melhor do Mês</span>
        </div>
      </div>
      <div class="action-item bg-danger-soft" @click.stop.prevent="imprimirDevedores()">
        <div class="action-icon">
          <i class="fa fa-bell text-danger"></i>
        </div>
        <div class="action-data">
          <span class="value">{{vendedor.TX_VL_CLIENTE_MAIS_DEVE}}</span>
          <span class="label">Mais Deve</span>
        </div>
      </div>
    </div>

    <!-- Lista de Pedidos -->
    <div class="orders-section">
      <div class="section-header d-flex justify-content-between align-items-center mb-3">
        <h5 class="mb-0">Pedidos Recentes</h5>
        <div class="filter-controls d-flex">
          <b-button size="sm" pill :variant="filtrou ? 'danger' : (isDark ? 'dark' : 'light')" @click="filtrou ? limparFiltro() : $bvModal.show('modal-filtro')" class="mr-2 shadow-sm">
            <i :class="filtrou ? 'fa fa-times' : 'fa fa-filter'"></i>
          </b-button>
          <b-button size="sm" pill :variant="isDark ? 'dark' : 'light'" @click="exportarHtml" class="shadow-sm">
            <i class="fa fa-print"></i>
          </b-button>
        </div>
      </div>

      <!-- Barra de Datas Compacta -->
      <div class="date-bar mb-3 shadow-sm">
        <div class="date-input-group">
          <i class="fa fa-calendar-o"></i>
          <input type="date" v-model="dt1">
        </div>
        <div class="date-separator">até</div>
        <div class="date-input-group">
          <input type="date" v-model="dt2">
        </div>
      </div>

      <!-- Card-based List -->
      <div v-if="lista && lista.length" class="order-list">
        <div 
          v-for="(item, index) in paginatedItems" 
          :key="index" 
          class="order-card-luxury shadow-sm mb-3"
          @click="edit([item])"
        >
          <!-- Header com cor do status (suave) -->
          <div :class="['card-luxury-header', 'status-' + variantSituacao(item)]">
            <span class="status-name">{{traduzirSituacao(item)}}</span>
            <div class="header-metrics">
              <span class="h-date">{{formatarData(item.DT_PEDIDO)}}</span>
              <span class="h-price">{{numberToReal(valorPedido(item))}}</span>
            </div>
          </div>

          <div class="card-luxury-body">
            <!-- Nome do Cliente em Linha Completa -->
            <div class="customer-full-name mb-2">
              {{item.NOME_CLIENTE}}
            </div>

            <div class="luxury-content">
              <!-- Lado Esquerdo: Identificação -->
              <div class="lux-section-identity">
                <div class="order-id">PEDIDO: <strong>{{item.SEQUENCIA}}</strong></div>
                <div class="items-preview">{{item.ITENS_VENDA}}</div>
              </div>

              <!-- Meio: Financeiro Aprofundado -->
              <div class="lux-section-finance">
                <div v-if="item.SITUACAO == '1' && item.VALOR_EM_ABERTO != 0" class="f-item">
                  <span class="f-label">Vencimento</span>
                  <span :class="['f-value', variantVencimento(item.VENCIMENTO, item.VALOR_EM_ABERTO)]">{{formatarData(item.VENCIMENTO)}}</span>
                </div>
                <div v-if="item.VALOR_EM_ABERTO != 0" class="f-item mt-1">
                  <span class="f-label">Débito</span>
                  <span :class="['f-value', variantVencimento(item.VENCIMENTO, item.VALOR_EM_ABERTO)]">{{numberToReal(item.VALOR_EM_ABERTO)}}</span>
                </div>
              </div>

              <!-- Direita: Ações de Apoio -->
              <div class="lux-section-actions">
                <div v-if="item.SITUACAO == '1'" class="payment-status mb-1">
                  <div v-if="item.VALOR_EM_ABERTO != 0" class="last-move text-right">
                    <span class="lbl">Ult. Mov.</span>
                    <span class="val">{{ item.ULT_BAIXA_DIAS == -1 ? 'Sem Mov.' : item.ULT_BAIXA_DIAS + ' D' }}</span>
                  </div>
                  <!-- Corrigido: Exibição do "QUITADO" quando faturado e sem débito -->
                  <div v-else class="text-success small quitado-label font-weight-bold">
                    <i class="fa fa-check-circle"></i> QUITADO
                  </div>
                </div>

                <b-button size="sm" variant="light" @click.stop.prevent="imprimir(item)" class="lux-btn-print">
                  <i class="fa fa-print"></i>
                </b-button>
              </div>
            </div>
            
            <b-progress v-if="item.VALOR_EM_ABERTO != 0" height="4px" class="progress-lux mt-2" v-model="item.PERCENTUAL" :variant="variantProgressoItem(item)"></b-progress>
          </div>
        </div>

        <div class="d-flex justify-content-center mt-3">
          <b-pagination
            v-model="currentPage"
            :total-rows="lista.length"
            :per-page="perPage"
            align="center"
            size="sm"
            pill
          ></b-pagination>
        </div>
      </div>

      <div v-else :class="['text-center p-5 rounded shadow-sm', isDark ? 'bg-dark text-light' : 'bg-light text-muted']">
        <i class="fa fa-folder-open-o fa-3x mb-3"></i>
        <p>Nenhum pedido encontrado no período.</p>
      </div>
    </div>

    <!-- Modal Filtro -->
    <b-modal id="modal-filtro" ref="modal-filtro" title="Filtro Avançado" centered hide-footer 
      :header-bg-variant="isDark ? 'dark' : 'light'" 
      :header-text-variant="isDark ? 'light' : 'dark'" 
      :body-bg-variant="isDark ? 'dark' : 'light'" 
      :body-text-variant="isDark ? 'light' : 'dark'">
      <div class="p-2">
        <b-form-group label="Situação do Pedido" label-class="font-weight-bold">
          <b-form-select 
            v-model="tipoSituacao"
            :options="filtro.statusOptions"
            class="modern-select"
          />
        </b-form-group>

        <b-form-group label="Cliente" label-class="font-weight-bold">
          <cliente-pesquisa 
            @retornaId="clienteId = $event"
            :idInicial="clienteId"
            label=""
            place="Pesquisar cliente..."
            labelBotao=""
            ref="clientepesquisa"
            idModal="clientepesquisa"
          />
        </b-form-group>

        <div class="d-flex justify-content-end mt-4">
          <b-button variant="light" @click="$bvModal.hide('modal-filtro')" class="mr-2 px-4">Fechar</b-button>
          <b-button variant="primary" @click="clicouFiltrar()" class="px-4 shadow-sm">Aplicar Filtro</b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment'
import { printHtmlReport} from '@/utils/RelatorioHTML'
import ClientePesquisa from '../pesquisa/ClientePesquisa.vue'
import Tools from '@/tools/tools.js'

export default {
  name: 'painelVendedor',
  components: {
    ClientePesquisa
  },
  data () {
    return {
      msg: 'painelVendedor',
      periodo: 'Ontem até hoje',
      periodoValue : '-2',
      filtro: {
        statusOptions: [
          {text: 'TODAS', value: null},
          {text: 'CANCELADA', value: '0'},
          {text: 'FECHADO', value: '1'},
          {text: 'LANÇANDO', value: '2'},
          {text: 'AGUARD. ORDEM', value: '3'},
          {text: 'ORDEM EXP.', value: '4'},
          {text: 'EMBARCANDO', value: '5'},
          {text: 'CONFERIDO / PRONTO P/ VENDA', value: '6'},
          {text: 'AGUARDANDO SEPARACAO', value: '7'},
          {text: 'EM SEPARACAO', value: '8'}
        ],
      },
      vendedor: {
        CODIGO: null,
        NOME: null,
        VL_META: null,
        TX_META: null,
        VL_TOTAL_VENDA: null,
        TX_TOTAL_VENDA : null,
        PERCENTUAL_META : 0.00,
        TX_PERCENTUAL_META: null,
        NOME_CLIENTE_MAIS_COMPROU: '',
        VL_CLIENTE_MAIS_COMPROU: 0.00,
        TX_VL_CLIENTE_MAIS_COMPROU: '0.00',
        NOME_CLIENTE_MAIS_DEVE: '',
        VL_CLIENTE_MAIS_DEVE: 0.00,
        TX_VL_CLIENTE_MAIS_DEVE: '0.00'
      },
      status: '',
      isBusy: false,
      fields: [
          {
            key: "NOME_CLIENTE",
            label: 'Cliente',
            sortable: true
          },
          {
            key: "valor",
            label: 'Resumos',
            sortable: true
          },
          {
            key: "dias",
            label: 'Valor',
            sortable: true
          }
        ],
      impressao: {
        fields: [
          {
            key: "SEQUENCIA",
            label: 'Pedido'
          },
          {
            key: "DT_PEDIDO",
            label: 'Data',
            type:'date',
          },
          {
            key: "DT_SAIDA",
            label: 'Faturamento',
            type:'date',
          },
          {
            key: "COD_CLI",
            label: 'Cliente ID'
          },
          {
            key: "NOME_CLIENTE",
            label: 'Cliente'
          },
          {
            key: "SITUACAO_TRADUZIDO",
            label: 'Situação'
          },
          {
            key: "ULT_BAIXA_DIAS",
            label: 'Ult. Baixa Dias',
          },
          {
            key: "VALOR_EM_ABERTO",
            label: 'Valor em Aberto',
            type:'currency', 
          },
          {
            key: "VALOR_PEDIDO_EXIBICAO",
            label: 'Valor',
            type:'currency', 
          }
        ],
      },
      currentPage: 1,
      perPage: 50,
      sortBy: null,
      sortDesc: false,
      filter: null,
      modalDetails: { index: '', data: '' },
      columns: [
        {title: 'ID', dataKey: 'id'},
        {title: 'Name', dataKey: 'name'},
        {title: 'Country', dataKey: 'country'}
      ],
      rows: [
        {'id': 1, 'name': 'Shaw', 'country': 'Tanzania'},
        {'id': 2, 'name': 'Nelson', 'country': 'Kazakhstan'},
        {'id': 3, 'name': 'Garcia', 'country': 'Madagascar'}
      ]
    }
  },
  methods:{
    exportarHtml () {
      const rows = (this.lista || []).map(item => ({
        ...item,
        VALOR_PEDIDO_EXIBICAO: this.valorPedido(item)
      }))
      printHtmlReport({
        rows: rows,                 // seu array de dados
        columns: this.impressao.fields,
        systemName: 'CECWeb',
        reportTitle: 'Pedidos do Vendedor ' + this.vendedor.NOME ,
        printedBy: window.localStorage.getItem('login') ?? '—',
        observations: `Período: ${this.dt1} a ${this.dt2}`,
        pageSize: 'A4',
        landscape: true,                    // vira “deitado” se quiser
        footerTotals: [
          // soma só a coluna DEBITO (usa key da coluna)
          { key:'VALOR_PEDIDO_EXIBICAO', formatter: v => v.toLocaleString('pt-BR',{style:'currency',currency:'BRL'}) },
          { key:'VALOR_EM_ABERTO', formatter: v => v.toLocaleString('pt-BR',{style:'currency',currency:'BRL'}) }
        ],
      });
    },
    getIniciais(nome) {
      if (!nome) return ''
      const partes = nome.trim().split(' ')
      const iniciais = partes.length >= 2
        ? partes[0][0] + partes[1][0]
        : partes[0].slice(0, 2)
      return iniciais.toUpperCase()
    },
    getCor(id) {
      const cores = ['primary', 'info', 'success', 'danger', 'warning', 'secondary', 'dark']
      return cores[id % cores.length]
    },
    consultarCliente() {
      this.$router.push(`/consultas/areceberporcliente`)
    },
    numberToReal (numero) {
      if ((typeof numero === 'undefined') || (numero === null) || (numero === '')) {
        return '0,00'
      }
      numero = Number(numero) || 0
      let n = numero.toFixed(2).split('.')
      n[0] = 'R$ ' + n[0].split(/(?=(?:...)*$)/).join('.')
      return n.join(',')
    },
    valorPedido(item) {
      if (!item) return 0
      if (String(item.SITUACAO || '').trim() !== '1') {
        const valorPrevisto = Number(item.VL_TOTAL_PREVISTO || 0)
        if (valorPrevisto > 0) return valorPrevisto
      }
      return Number(item.VL_TOTAL_NFE || 0)
    },
    colorirPercentualMeta() {
      if (this.vendedor.PERCENTUAL_META >= 100) return 'green'
      else 'danger'
    },
    abrirNovoPedido() {
      this.$router.push(`/movimentos/pedidovenda/add`)
    },
    variantVencimento (vencimento, valor) {
      if ((typeof valor === 'undefined') || (valor === null) || (valor === '')) {
        return 'text-muted'
      } else  if ((typeof vencimento === 'undefined') || (vencimento === null) || (vencimento === '')) {
          return 'text-muted'
      }

      const date = moment(vencimento, ['DD/MM/YYYY', 'YYYY-MM-DD'])
      if (!date.isValid()) return 'text-muted'

      let $variant
      if (date.isBefore(moment(), 'day')) {
        $variant = 'text-red'
      } else {
        $variant = 'text-blue'
      }
      return $variant
    },
    variantProgresso (value) {
      if ((typeof value === 'undefined') || (value === null) || (value === '')) {
        return 'muted'
      } 
      const date = moment(value, ['DD/MM/YYYY', 'YYYY-MM-DD'])
      if (!date.isValid()) return 'muted'

      let $variant
      if (date.isBefore(moment(), 'day')) {
        $variant = 'danger'
      } else {
        $variant = 'info'
      }
      return $variant
    },
    variantProgressoItem (item) {
      if (!item.VENCIMENTO) return 'muted'
      const date = moment(item.VENCIMENTO, ['DD/MM/YYYY', 'YYYY-MM-DD'])
      if (!date.isValid()) return 'muted'
      
      const isAtrasado = date.isBefore(moment(), 'day')
      // Se fechado (1) e atrasado -> Perigo (Vermelho)
      if (item.SITUACAO == '1' && isAtrasado) return 'danger'
      // Se apenas atrasado mas não fechado -> Perigo também
      return isAtrasado ? 'danger' : 'info'
    },
    variantSituacao (registro) {
      const item = registro && typeof registro === 'object' ? registro : null
      const value = item ? item.SITUACAO : registro
      let $variant
      if ( value == '0'  ) {
        $variant = 'danger'
      } else if ( value == '1'  ) {
        if (item && this.isEntregaConfirmada(item)) $variant = 'success'
        else if (!item || !item.ORDEM_ENTREGA_ID) $variant = 'success'
        else {
          const statusOrdem = this.getStatusOrdemEntrega(item)
          if (statusOrdem === '1') $variant = 'primary'
          else if (['2', '3', '4'].includes(statusOrdem)) $variant = 'info'
          else $variant = 'primary'
        }
      } else if ( value == '2'  ) {
        $variant = 'warning'
      } else if ( value == '3'  ) {
        $variant = 'info'
      } else if ( value == '4'  ) {
        $variant = 'warning'
      } else if ( value == '5'  ) {
        $variant = 'info'
      } else if ( value == '6'  ) {
        $variant = 'primary'
      }
      return $variant
    },
    formatarData (data) {
      if (!data) return ''
      return moment(data, ['DD/MM/YYYY', 'YYYY-MM-DD']).format('DD/MM/YYYY')
    },
    getStatusOrdemEntrega (item) {
      return String((item && item.ORDEM_ENTREGA_STATUS) || '').trim()
    },
    isEntregaConfirmada (item) {
      return String((item && item.OP_RETORNO_ENTREGA) || '0').trim() === '1'
    },
    traduzirSituacao (registro) {
      const item = registro && typeof registro === 'object' ? registro : null
      const value = item ? item.SITUACAO : registro
      let $variant
      if ( value == '0'  ) {
        $variant = 'CANCELADO'
      } else if ( value == '1'  ) {
        if (item && this.isEntregaConfirmada(item)) $variant = 'ENTREGUE'
        else if (!item || !item.ORDEM_ENTREGA_ID) $variant = 'FECHADO / AGUARD. CARGA'
        else {
          const statusOrdem = this.getStatusOrdemEntrega(item)
          if (statusOrdem === '1') $variant = 'EM EMBARQUE'
          else if (['2', '3', '4'].includes(statusOrdem)) $variant = 'EMBARCADO'
          else $variant = 'EM EMBARQUE'
        }
      } else if ( value == '2'  ) {
        $variant = 'EM CADASTRO'
      } else if ( value == '3'  ) {
        $variant = 'AGUARDANDO'
      } else if ( value == '4'  ) {
        $variant = 'ORDEM EXPEDIÇÃO'
      } else if ( value == '5'  ) {
        $variant = 'EMBARCANDO'
      } else if ( value == '6'  ) {
        $variant = item && String(item.OP_CONFERENCIA_VENDA || '0').trim() === '1' ? 'CONFERIDO / PRONTO P/ VENDA' : 'EMBARCADO'
      } else if ( value == '7'  ) {
        $variant = 'AGUARDANDO SEPARACAO'
      } else if ( value == '8'  ) {
        $variant = 'EM SEPARACAO'
      }
      return $variant
    },
    traduzSituacao (value) {
      return this.traduzirSituacao(value)
    },
    edit (item) {
      this.$store.commit('controle/setAguardando', true)
      this.$router.push(`/movimentos/pedidovenda/edit/${item[0].SEQUENCIA}`)
    },
    async imprimir (item) {
      console.log(item.SITUACAO)
      if (item.SITUACAO == '1')
        await this.imprimirFatura(item.SEQUENCIA)
      else
        await this.imprimirPedido(item.SEQUENCIA)
    },

    async imprimirFatura (id) {
      try {
        this.$store.commit('controle/setAguardando', true)

        try {
          this.$store.commit("venda/SET_REGISTRO_SELECIONADO_ID", id)
          const relPayLoad = {
            id: id,
            login: window.localStorage.getItem('login')
            
          }
          const response =   await this.$http.post(`/api/protected/relatorios/relvenda`, relPayLoad, { responseType: 'arraybuffer' })
          
          const url = window.URL.createObjectURL(new Blob([response.data], {type: 'application/pdf'}))
          const link = document.createElement('a')
          
          link.href = url
          link.setAttribute('download', 'relVenda.pdf') 
          link.download = 'relVenda.pdf'
          const janela = window.open(link)
          janela.onload = function(){
            setTimeout(() => {
              janela.document.title = id +' Relatório de Vendas'
            }, 2000);
          }

          Tools.exibirMensagem(this.$store, 'Aviso', 'success', 'Relatório Gerado com Sucesso!!!' )
        } catch(err) {
          Tools.exibirMensagem(this.$store, 'Aviso', 'warning', 'Relatório Indisponível!!!' )
        }
      } finally {
        this.$store.commit('controle/setAguardando', false)
      }
    },
    async imprimirPedido (id) {
      this.$store.commit('controle/setAguardando', true)
      let uri = '/api/protected/relatorios/relvendapedido?id=' + id
      this.$http.get(uri, {responseType: 'arraybuffer'})
        .then(response => {
          this.$store.commit('controle/setAguardando', false)
          const url = window.URL.createObjectURL(new Blob([response.data], {type: 'application/pdf'}))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', 'relVendaPedido.pdf') 
          link.download = 'relVendaPedido.pdf'
          // document.body.appendChild(link)
          //link.click()
          // document.body.removeChild(link)
          const janela = window.open(link)
          /*
          setTimeout(() =>
            { 
                janela.document.title = 'Relatório de Vendas'; 
            }, 1000);
            */
          
          janela.onload = function(){
            setTimeout(() => {
              janela.document.title = id +' Relatório de Pedido de Venda'
            }, 2000);
          }

          this.$root.$bvToast.toast(`Relatório Gerado com sucesso!!!`, {
            title: 'Aviso',
            autoHideDelay: 2000,
            variant: 'success',
            appendToast: true
          })
          
        })
        .catch(erro => {
          this.$store.commit('controle/setAguardando', false)
          this.$toast.error({
            title: 'Erro',
            message: 'Ocorreram erros ao gerar o Relatorio ',
            progressBar: false,
            showMethod: 'shake',
            position: 'top full width'
          })
        })
    },
    imprimirDevedores () {
      let dt1 = '01/01/2000'
      let dt2 = new Date().toISOString().substr(0, 10).split('-').reverse().join('/')
      let tipoRel = 'A'
      let tipoPeriodo = 'V'
      this.$store.commit('controle/setAguardando', true)
      let uri = '/api/protected/relatorios/relareceber?vendedorid=' + window.localStorage.getItem('vendedorId') +
        '&lbvendedor=' + this.vendedor.NOME +
        '&lbempresa=' + window.localStorage.getItem('nomeempresa') +
        '&dt1=' + dt1 +
        '&dt2=' + dt2 +
        '&lbusuario=' + window.localStorage.getItem('nomeusuario') +
        '&tiporel=' +tipoRel+
        '&tipoperiodo=' + tipoPeriodo
      this.$http.get(uri, {responseType: 'arraybuffer'})
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data], {type: 'application/pdf'}))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'relatório de Contas a Receber.pdf')
        link.download = 'relatorioAreceber.pdf'
        // document.body.appendChild(link)
        //link.click()
        // document.body.removeChild(link) 
        const janela = window.open(link)
        /*
        setTimeout(() =>
          { 
              janela.document.title = 'Relatório de Vendas'; 
          }, 1000);
          */
        janela.onload = function(){
          setTimeout(() => {
            janela.document.title = 'Relatório de Contas a Receber'
          }, 2000)
        }
        this.$store.commit('controle/setAguardando', false)
        this.$toast.success({
          title: 'Sucesso',
          message: 'Relatório Gerado com Sucesso ',
          progressBar: false,
          timeOut: 300,
          showMethod: 'shake',
          position: 'top full width'
        })
      })
      .catch(erro => {
        this.$store.commit('controle/setAguardando', false)
        this.$root.$bvToast.toast(`Ocorreu uma falha ao gerar o relatório!!!`, {
          title: 'Aviso',
          autoHideDelay: 2000,
          variant: 'danger',
          appendToast: true
        })
        this.isLoading = false
      })
    }, 
    imprimirVendas () {
     
     const currentDate = moment();
     const firstDayOfMonth = currentDate.startOf('month');
     const dt1 = firstDayOfMonth.format('MM/DD/YYYY');
     let dt2 = new Date().toISOString().substr(0, 10).split('-').reverse().join('/')
     let tipoRelatorio = '1'
     let tipoBoleto = ''
     let formaPagamento = 'Todos'
     let tipoPeriodo = 'D'
     let outros = 'V'
     let ordem = '1'

     this.$store.commit('controle/setAguardando', true)
     let uri = '/api/protected/relatorios/venda/analitico?dt1=' + dt1 + '&dt2=' + dt2 +
       '&vendedorId=' + window.localStorage.getItem('vendedorId') +
       '&lbVendedor=' + this.vendedor.NOME +
       '&tipoBoleto=' + tipoBoleto +
       '&formaPagamento=' + formaPagamento +
       '&tipoPeriodo=' + tipoPeriodo +
       '&outros=' + outros +
       '&ordem=' + ordem +
       '&lbEmpresa=' + window.localStorage.getItem('nomeempresa') +
       '&lbUsuario=' + window.localStorage.getItem('nomeusuario') +
       '&tipoRel=' + tipoRelatorio
       
     this.$http.get(uri, {responseType: 'arraybuffer'})
     .then(response => {
       
       const url = window.URL.createObjectURL(new Blob([response.data], {type: 'application/pdf'}))
       const link = document.createElement('a')
       link.href = url
       link.setAttribute('download', 'relVendaAnalitico.pdf') 
       link.download = 'RelVendaAnalitico.pdf'
       // document.body.appendChild(link)
       //link.click()
       // document.body.removeChild(link)
       const janela = window.open(link)
       /*
       setTimeout(() =>
         { 
             janela.document.title = 'Relatório de Vendas'; 
         }, 1000);
         */
       janela.onload = function(){
         setTimeout(() => {
           janela.document.title = id +' Relatório de Vendas Analitico'
         }, 2000);
       }
       this.$store.commit('controle/setAguardando', false)
       this.$root.$bvToast.toast('Relatório Gerado com Sucesso!', {
         title: 'Sucesso',
         autoHideDelay: 3000,
         variant: 'success',
         appendToast: true
       }) 
     })
     .catch(erro => {
       this.$toast.error({
         title: 'Erro',
         message: 'Ocorreram erros ao gerar o Relatorio ',
         progressBar: false,
         showMethod: 'shake',
         position: 'top full width'
       })
       this.$store.commit('controle/setAguardando', false)
     })
   },
   async clicouFiltrar() {
    await this.filtrar()
    this.filtrou = true
   },
   async limparFiltro() {
    
    this.dt1 = moment().subtract(7, 'days').format('YYYY-MM-DD')
    this.dt2 = moment().format('YYYY-MM-DD')
    this.clienteId = null
    this.tipoSituacao = null
    this.filtrou = false
    await this.filtrar()
    
   },
   async filtrar() {
    this.$store.commit('controle/setAguardando', true)
    try {
      await this.$store.dispatch('vendaPedido/GERAR_LISTA')
    } catch (err) {
      if (err && err.response && err.response.status == 400) {
        this.toastCount++
        this.$root.$bvToast.toast(`Atenção: ${err.response.data}`, {
          title: 'Atenção',
          autoHideDelay: 3000,
          variant: 'danger',
          appendToast: true
        })
      }
    } finally {
      this.$refs['modal-filtro'].hide()
      this.$store.commit('controle/setAguardando', false)
    }
   }
  },
  computed: {
    isDark() {return this.$store.getters['tema/IS_DARK']  },
    lista: {
      get () {
        return this.$store.getters['vendaPedido/LISTA']
      }, 
      set (value) {
        this.$store.commit("vendaPedido/SET_LISTA", value)
      }
    },
    dt1: {
      get () {
        return this.$store.getters['vendaPedido/DT1']
      }, 
      set (value) {
        this.$store.commit("vendaPedido/SET_DT1", value);
      }
    },
    dt2: {
      get () {
        return this.$store.getters['vendaPedido/DT2']
      }, 
      set (value) {
        this.$store.commit("vendaPedido/SET_DT2", value);
      }
    },
    tipoSituacao: {
      get () {
        return this.$store.getters['vendaPedido/TIPO_SITUACAO']
      }, 
      set (value) {
        this.$store.commit("vendaPedido/SET_TIPO_SITUACAO", value)
      }
    },
    clienteId: {
      get () {
        return this.$store.getters['vendaPedido/CLIENTE_ID']
      }, 
      set (value) {
        this.$store.commit("vendaPedido/SET_CLIENTE_ID", value)
      }
    },
    filtrou: {
      get () {
        return this.$store.getters['vendaPedido/FILTROU']
      }, 
      set (value) {
        this.$store.commit("vendaPedido/SET_FILTROU", value)
      }
    },
    paginatedItems() {
      if (!this.lista) return []
      const start = (this.currentPage - 1) * this.perPage
      const end = start + this.perPage
      return this.lista.slice(start, end)
    }

  },
  watch: {
    'dt1': async function () {
      await this.filtrar()
    },
    'dt2': async function (newVal, oldVal) {
      // evita loop quando atribuimos de volta o mesmo valor
      if (newVal === oldVal) return

      // valida formato da data (YYYY-MM-DD) e limite mínimo (1 ano atrás)
      const minDate = moment().subtract(1, 'years').startOf('day')
      const isValid = moment(newVal, 'YYYY-MM-DD', true).isValid()

      if (!isValid) {
        this.$root.$bvToast.toast('Data inválida.', {
          title: 'Atenção',
          autoHideDelay: 3000,
          variant: 'danger',
          appendToast: true
        })
        // restaura valor anterior (ou define para o mínimo permitido)
        this.dt2 = oldVal || minDate.format('YYYY-MM-DD')
        return
      }

      if (moment(newVal, 'YYYY-MM-DD').isBefore(minDate, 'day')) {
        this.$root.$bvToast.toast(`A data não pode ser anterior a ${minDate.format('YYYY-MM-DD')}.`, {
          title: 'Atenção',
          autoHideDelay: 3000,
          variant: 'danger',
          appendToast: true
        })
        this.dt2 = oldVal || minDate.format('YYYY-MM-DD')
        return
      }

      // se passar na validação, executa a ação
      await this.filtrar()
    }
    
  },
  async created() {
    this.$store.commit('controle/setAguardando', true)
    this.dt1 = moment().subtract(7, 'days').format('YYYY-MM-DD')
    this.dt2 = moment().format('YYYY-MM-DD')

    this.vendedor.TX_META = 'INDEFINIDA'
    this.vendedor.VL_TOTAL_VENDA = 0.00
    this.vendedor.TX_TOTAL_VENDA = 'SEM VENDAS'
    this.vendedor.PERCENTUAL_META = 0.00
    this.vendedor.TX_PERCENTUAL_META = '0.00'

    this.vendedor.NOME_CLIENTE_MAIS_COMPROU = ''
    this.vendedor.VL_CLIENTE_MAIS_COMPROU = 0.00
    this.vendedor.TX_VL_CLIENTE_MAIS_COMPROU = '0.00'
    this.vendedor.NOME_CLIENTE_MAIS_DEVE = ''
    this.vendedor.VL_CLIENTE_MAIS_DEVE = 0.00
    this.vendedor.TX_VL_CLIENTE_MAIS_DEVE = '0.00'

    try {
      // vendedor info
      const resVendedor = await this.$http.get(`/api/protected/vendedor/getById/` + window.localStorage.getItem('vendedorId'))
      const vd = (resVendedor.data && resVendedor.data[0]) || {}
      this.vendedor.CODIGO = vd.CODIGO
      this.vendedor.NOME = vd.NOME
      this.vendedor.VL_META = vd.VL_META

      if (this.vendedor.VL_META && this.vendedor.VL_META > 0) {
        this.vendedor.TX_META = this.vendedor.VL_META.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
      }

      // total venda no mês
      const resTotal = await this.$http.get(`/api/protected/venda/getTotalVendaNoMesPorVendedor/` + window.localStorage.getItem('vendedorId'))
      if (resTotal.data && resTotal.data[0]) {
        const t = resTotal.data[0]
        if (t.VL_TOTAL) {
          this.vendedor.TX_TOTAL_VENDA = t.VL_TOTAL.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
          this.vendedor.VL_TOTAL_VENDA = t.VL_TOTAL
        }
        if ((this.vendedor.VL_META > 0) && (this.vendedor.VL_TOTAL_VENDA > 0)) {
          this.vendedor.PERCENTUAL_META = (this.vendedor.VL_TOTAL_VENDA / this.vendedor.VL_META) * 100
          this.vendedor.TX_PERCENTUAL_META = this.vendedor.PERCENTUAL_META.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        }
      }

      // cliente que mais comprou
      const resMaisComprou = await this.$http.get(`/api/protected/venda/getClienteMaisComprouNoMesPorVendedor/` + window.localStorage.getItem('vendedorId'))
      this.vendedor.NOME_CLIENTE_MAIS_COMPROU = ''
      this.vendedor.VL_CLIENTE_MAIS_COMPROU = 0.00
      this.vendedor.TX_VL_CLIENTE_MAIS_COMPROU = '0.00'
      if (resMaisComprou.data && resMaisComprou.data.length) {
        const mc = resMaisComprou.data[0]
        this.vendedor.NOME_CLIENTE_MAIS_COMPROU = mc.NOME
        if (mc.VL_TOTAL) {
          this.vendedor.TX_VL_CLIENTE_MAIS_COMPROU = mc.VL_TOTAL.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
          this.vendedor.VL_CLIENTE_MAIS_COMPROU = mc.VL_TOTAL
        }
      }

      // cliente que mais deve
      const resMaisDeve = await this.$http.get(`/api/protected/areceber/getClienteMaisDevePorVendedor/` + window.localStorage.getItem('vendedorId'))
      this.vendedor.NOME_CLIENTE_MAIS_DEVE = ''
      this.vendedor.VL_CLIENTE_MAIS_DEVE = 0.00
      this.vendedor.TX_VL_CLIENTE_MAIS_DEVE = '0.00'
      if (resMaisDeve.data && resMaisDeve.data.length) {
        const md = resMaisDeve.data[0]
        this.vendedor.NOME_CLIENTE_MAIS_DEVE = md.NOME_CLIENTE
        if (md.VL_TOTAL) {
          this.vendedor.TX_VL_CLIENTE_MAIS_DEVE = md.VL_TOTAL.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
          this.vendedor.VL_CLIENTE_MAIS_DEVE = md.VL_TOTAL
        }
      }

      // gerar lista
      await this.$store.dispatch('vendaPedido/GERAR_LISTA')
    } catch (err) {
      if (err && err.response && err.response.status == 400) {
        this.toastCount++
        this.$root.$bvToast.toast(`Atenção: ${err.response.data}`, {
          title: 'Atenção',
          autoHideDelay: 3000,
          variant: 'danger',
          appendToast: true
        })
      } else {
        this.$root.$bvToast.toast(`Ocorreu um erro: ${err && err.message}`, {
          title: 'Erro',
          autoHideDelay: 3000,
          variant: 'danger',
          appendToast: true
        })
      }
    } finally {
      this.$store.commit('controle/setAguardando', false)
    }
  }
}
</script>

<style scoped lang="scss">
.dashboard-vendedor {
  padding: 10px;
  background-color: #f8f9fa;
  min-height: 100vh;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

/* Profile Section */
.profile-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  color: #1a202c;
  padding: 18px 20px;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
  position: relative;
  overflow: hidden;
  border: 1px solid #edf2f7;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -10%;
    width: 200px;
    height: 200px;
    background: rgba(0, 0, 0, 0.02);
    border-radius: 50%;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -20%;
    left: -5%;
    width: 120px;
    height: 120px;
    background: rgba(0, 0, 0, 0.015);
    border-radius: 50%;
  }

  .profile-info {
    .name {
      font-weight: 700;
      color: #2d3748;
      font-size: 1.2rem;
      letter-spacing: -0.5px;
    }
    .meta-label {
      font-size: 0.72rem;
      color: #718096;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      font-weight: 600;
      span {
        font-weight: 800;
        color: #2d3748;
        margin-left: 4px;
      }
    }
  }

  .progress-container {
    margin-top: 10px;
    .small {
      color: #718096;
      font-weight: 500;
      font-size: 0.75rem;
      .font-weight-bold {
        color: #2d3748;
      }
    }
  }
}

.progress-modern {
  border-radius: 12px;
  background-color: #edf2f7;
}

/* Actions Grid */
.actions-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.action-item {
  padding: 15px;
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.03);
  transition: transform 0.2s, box-shadow 0.2s;
  border: 1px solid rgba(0,0,0,0.02);

  &:active {
    transform: scale(0.96);
  }

  .action-icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.15rem;
    margin-bottom: 10px;
    background: white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.05);
  }

  .action-label {
    font-weight: 700;
    font-size: 0.8rem;
    color: #2d3748;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    margin-top: 2px;
    text-align: center;
  }

  .action-data {
    display: flex;
    flex-direction: column;
    align-items: center;
    .value {
      font-weight: 800;
      font-size: 1rem;
      color: #1a202c;
      letter-spacing: -0.5px;
      text-align: center;
    }
    .label {
      font-size: 0.6rem;
      color: #718096;
      text-transform: uppercase;
      font-weight: 700;
      letter-spacing: 0.5px;
      text-align: center;
    }
  }
}

/* Utility Backgrounds */
.bg-warning-soft { background-color: #fffaf0 !important; border-color: #feebc8 !important; }
.bg-success-soft { background-color: #f0fff4 !important; border-color: #c6f6d5 !important; }
.bg-primary-soft { background-color: #ebf8ff !important; border-color: #bee3f8 !important; }
.bg-danger-soft { background-color: #fff5f5 !important; border-color: #fed7d7 !important; }

/* Date Bar */
.date-bar {
  background: white;
  border-radius: 12px;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  .date-input-group {
    display: flex;
    align-items: center;
    flex: 1;
    
    i {
      color: #a0aec0;
      margin-right: 8px;
    }
    
    input {
      border: none;
      font-size: 0.85rem;
      font-weight: 600;
      color: #4a5568;
      width: 100%;
      &:focus { outline: none; }
    }
  }
  
  .date-separator {
    padding: 0 10px;
    font-size: 0.75rem;
    color: #cbd5e0;
    font-weight: bold;
  }
}

/* Luxury Order Card Design */
.order-card-luxury {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #edf2f7;
  box-shadow: 0 1px 3px rgba(0,0,0,0.02);
  
  &:active { background-color: #f8fafc; }
}

/* Softer Status Colors */
.status-success { background-color: #f0fff4; color: #38a169; border-bottom: 1px solid #c6f6d5; }
.status-danger { background-color: #fff5f5; color: #e53e3e; border-bottom: 1px solid #fed7d7; }
.status-warning { background-color: #fffaf0; color: #dd6b20; border-bottom: 1px solid #feebc8; }
.status-info { background-color: #ebf8ff; color: #3182ce; border-bottom: 1px solid #bee3f8; }
.status-primary { background-color: #f5f3ff; color: #7c3aed; border-bottom: 1px solid #ddd6fe; }
.status-secondary { background-color: #f7fafc; color: #4a5568; border-bottom: 1px solid #edf2f7; }

.card-luxury-header {
  padding: 5px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  .status-name {
    font-weight: 500;
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.8px;
  }
  
  .header-metrics {
    display: flex;
    align-items: center;
    gap: 15px;
    
    .h-date { font-size: 0.75rem; font-weight: 500; opacity: 0.9; }
    .h-price { font-size: 0.75rem; font-weight: 600; }
  }
}

.card-luxury-body {
  padding: 10px 12px;
  
  .customer-full-name {
    font-weight: 600;
    color: #1a202c;
    font-size: 0.9rem;
    line-height: 1.3;
    display: block;
    width: 100%;
  }

  .luxury-content {
    display: flex;
    gap: 12px;
    align-items: flex-start;
  }
}

.lux-section-identity {
  flex: 1.4;
  min-width: 0;
  
  .order-id {
    font-size: 0.65rem;
    color: #718096;
    margin-bottom: 2px;
    strong { color: #4a5568; }
  }
  .items-preview {
    font-size: 0.7rem;
    color: #718096;
    line-height: 1.2;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
}

.lux-section-finance {
  flex: 1.1;
  min-width: 0;
  padding: 0 10px;
  border-left: 1px solid #f1f5f9;
  border-right: 1px solid #f1f5f9;
  
  .f-item {
    display: flex;
    flex-direction: column;
    .f-label { font-size: 0.55rem; color: #a0aec0; text-transform: uppercase; font-weight: 600; }
    .f-value { font-size: 0.75rem; font-weight: 600; white-space: nowrap; }
  }
}

.lux-section-actions {
  flex: 0.6;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  
  .payment-status {
    width: 100%;
    .last-move {
      .lbl { font-size: 0.5rem; color: #a0aec0; display: block; line-height: 1; margin-bottom: 1px; }
      .val { font-size: 0.7rem; font-weight: 600; color: #4a5568; }
    }
  }
  
  .lux-btn-print {
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    color: #64748b;
    margin-top: 4px;
    font-size: 0.75rem;
  }
}

.progress-lux {
  margin-top: 10px;
  border-radius: 0 0 12px 12px;
}

/* Ajustes para telas muito pequenas */
@media (max-width: 380px) {
  .order-card-dense .card-content { gap: 4px; }
  .order-card-dense .section-identity .customer-name { font-size: 0.75rem; }
  .order-card-dense .section-finance .main-price .value { font-size: 0.75rem; }
}

/* Badge Tweaks */
.badge-pill {
  font-weight: 600;
  letter-spacing: 0.3px;
  font-size: 75%;
}

.text-red { color: #e53e3e !important; }
.text-blue { color: #3182ce !important; }

/* Custom Scrollbar for better UI */
::-webkit-scrollbar {
  width: 4px;
}
::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 10px;
}

.btn-print-order {
  border-radius: 10px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #718096;
  &:hover { color: #4a5568; }
}

.modern-select {
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  padding: 10px;
  height: auto;
  &:focus {
    border-color: #3182ce;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.15);
  }
}

/* Dark Mode Support */
.dashboard-dark {
  background-color: #1e1e2d;
  color: #e2e8f0;

  .profile-card {
    background: linear-gradient(135deg, #2d3748 0%, #1a202c 100%);
    border-color: #4a5568;
    color: #e2e8f0;

    .profile-info {
      .name { color: #f7fafc; }
      .meta-label { color: #a0aec0; span { color: #e2e8f0; } }
    }
    
    .progress-container .small {
      color: #a0aec0;
      .font-weight-bold { color: #f7fafc; }
    }
  }

  .progress-modern {
    background-color: #4a5568;
  }

  .action-item {
    border-color: rgba(255, 255, 255, 0.05);
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);

    .action-icon {
      background: #2d3748;
      box-shadow: 0 2px 6px rgba(0,0,0,0.2);
    }
    .action-label { color: #e2e8f0; }
    .action-data {
      .value { color: #f7fafc; }
      .label { color: #a0aec0; }
    }
  }

  /* Soft Backgrounds adapted for Dark Mode */
  .bg-warning-soft { background-color: rgba(221, 107, 32, 0.15) !important; border-color: rgba(221, 107, 32, 0.3) !important; }
  .bg-success-soft { background-color: rgba(56, 161, 105, 0.15) !important; border-color: rgba(56, 161, 105, 0.3) !important; }
  .bg-primary-soft { background-color: rgba(49, 130, 206, 0.15) !important; border-color: rgba(49, 130, 206, 0.3) !important; }
  .bg-danger-soft { background-color: rgba(229, 62, 62, 0.15) !important; border-color: rgba(229, 62, 62, 0.3) !important; }

  .orders-section {
    .section-header h5 { color: #e2e8f0; }
  }

  .date-bar {
    background: #2d3748;
    .date-input-group {
      i { color: #a0aec0; }
      input {
        color: #e2e8f0;
        background: transparent;
      }
      /* Optional: filter input calendar icon for dark mode in some browsers */
      input::-webkit-calendar-picker-indicator {
        filter: invert(1);
      }
    }
    .date-separator { color: #718096; }
  }

  .order-card-luxury {
    background: #2d3748;
    border-color: #4a5568;
    &:active { background-color: #1a202c; }

    .card-luxury-body {
      .customer-full-name { color: #f7fafc; }
      .order-id { color: #a0aec0; strong { color: #e2e8f0; } }
      .items-preview { color: #a0aec0; }
      
      .lux-section-finance {
        border-color: #4a5568;
        .f-item {
          .f-label { color: #a0aec0; }
          .f-value { 
            color: #e2e8f0; 
            &.text-blue { color: #63b3ed !important; }
            &.text-red { color: #fc8181 !important; }
            &.text-muted { color: #a0aec0 !important; }
          }
        }
      }

      .lux-section-actions {
        .payment-status .last-move {
          .lbl { color: #a0aec0; }
          .val { color: #e2e8f0; }
        }
        .lux-btn-print {
          background: #4a5568;
          border-color: #718096;
          color: #e2e8f0;
        }
        .quitado-label { color: #68d391 !important; }
      }
    }
  }

  .status-success { background-color: rgba(56, 161, 105, 0.2); color: #68d391; border-bottom-color: rgba(56, 161, 105, 0.4); }
  .status-danger { background-color: rgba(229, 62, 62, 0.2); color: #fc8181; border-bottom-color: rgba(229, 62, 62, 0.4); }
  .status-warning { background-color: rgba(221, 107, 32, 0.2); color: #f6ad55; border-bottom-color: rgba(221, 107, 32, 0.4); }
  .status-info { background-color: rgba(49, 130, 206, 0.2); color: #63b3ed; border-bottom-color: rgba(49, 130, 206, 0.4); }
  .status-primary { background-color: rgba(124, 58, 237, 0.2); color: #a78bfa; border-bottom-color: rgba(124, 58, 237, 0.4); }
  .status-secondary { background-color: rgba(74, 85, 104, 0.2); color: #a0aec0; border-bottom-color: rgba(74, 85, 104, 0.4); }

  .modern-select {
    background-color: #2d3748;
    border-color: #4a5568;
    color: #e2e8f0;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/dashboard/DashboardVendedor.vue